// ═══════════════════════════════════════════════════════════════════════════════
// MoonbeamCreatorShared.js
// ═══════════════════════════════════════════════════════════════════════════════
//
// Constants, utilities, and shared UI components used across the MoonbeamCreator
// send flow (MoonbeamCreator, MoonbeamCreatorRecipients, MoonbeamCreatorFinalize,
// MoonbeamCreatorModals).
//
// CHANGES IN THIS REVISION (Shareable Links & Reactions build):
//   - Step 2 label updated from "Finalize & Send" to "Finalize & Share" since
//     the final action can produce a link, an email send, or both.
//   - NEW: SHARE_SUGGESTIONS — chip suggestions for the link label input.
//   - NEW: buildShareSummary() — produces the dynamic "Here's what will
//     happen" text shown on Step 2 and in the post-share toast. Pure
//     function, no React, so it can be reused anywhere (creator, toast,
//     My eCards page).
//   - NEW: formatScheduledDateTime() — small date formatter used by the
//     summary builder so the same display style appears everywhere.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

export const CONTACTS_PER_PAGE = 40;

export const MONTHS = [
  { value: '', label: 'Month' },
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

export const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const TIMEZONE_REGION_ORDER = ['US', 'Canada', 'Europe', 'Americas', 'Middle East', 'Asia', 'Africa', 'Pacific', 'Other'];

export const SEND_STEPS = [
  { id: 1, label: 'How to Share' },
  { id: 2, label: 'Finalize & Share' },
];

// Suggestion chips shown under the shareable-link label input. Picked to
// cover the most common reasons a user would want to track a paste-in
// share: text/WhatsApp groups, social platforms, household groupings.
// Users can ignore them and type freely — these are just shortcuts.
export const SHARE_SUGGESTIONS = [
  'All grandkids',
  'Facebook group',
  'Family text',
  'Instagram bio',
  'Twitter',
];

// ─── Utility Functions ────────────────────────────────────────────────────────

export function daysInMonth(month) {
  if (!month) return 31;
  return [0, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month] || 31;
}

export function formatBirthday(birthmonth, birthdate) {
  if (!birthmonth || !birthdate) return null;
  return `${MONTH_ABBR[birthmonth]} ${birthdate}`;
}

export function escapeForJs(str) {
  return (str || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/'/g, "\\'").replace(/\n/g, '\\n').replace(/\r/g, '\\r');
}

export function buildPreviewHtml(template, musicId, musicOptions) {
  if (!template || !template.htmlcontent) return '';
  let html = template.preview_html || template.htmlcontent;
  let musicCode = '[]';
  let musicAudioUrl = '';
  if (musicId && musicOptions) {
    const selected = musicOptions.find((m) => String(m.musicid) === String(musicId));
    if (selected) {
      if (selected.musictype === 'audio' && selected.musicaudiourl) {
        musicAudioUrl = selected.musicaudiourl;
      } else if (selected.musiccode) {
        musicCode = selected.musiccode;
      }
    }
  }
  html = html.replace(/\{\{music_selection\}\}/g, musicCode);
  html = html.replace(/\{\{music_audio_url\}\}/g, escapeForJs(musicAudioUrl));
  return html;
}

export function isUnlimitedUsage(usage) {
  return !usage || usage.isUnlimited || usage.limit === -1;
}

// ─── Date Formatting ──────────────────────────────────────────────────────────

/**
 * Format a scheduled date/time consistently across the creator, the
 * post-share toast, and the summary block. Returns an empty string for
 * falsy input so callers can ${} it into a template literal safely.
 *
 * Output example: "Mar 15 at 9:00 AM"
 */
export function formatScheduledDateTime(dateValue) {
  if (!dateValue) return '';
  const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} at ${timePart}`;
}

/**
 * Format a birthday-mode time-of-day (24-hour "HH:MM") into a friendly
 * "9:00 AM" style string. Mirrors the formatBirthdayTimeLabel logic in
 * the recipients step so the summary uses the same phrasing.
 */
export function formatBirthdayTimeOfDay(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  const hour = parseInt(h);
  if (Number.isNaN(hour)) return '';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
}

// ─── Share Summary Builder ────────────────────────────────────────────────────

/**
 * Build the dynamic "Here's what will happen" summary shown on Step 2 of
 * the creator, and reused in the post-share toast on My eCards.
 *
 * Returns an array of summary lines, each: { icon, text }
 *   icon: emoji string (🔗 / ✉️ / 📅 / 🎂)
 *   text: the human-readable sentence
 *
 * Returning structured data (rather than a single HTML string) lets the
 * caller render each line however they want — separate <p>s in the
 * creator, single concatenated string in a one-line toast, etc.
 *
 * The order is always: shareable link first, then email, so the link is
 * presented as the primary action when both are happening. Matches the
 * stacking order in Step 1 (link section on top).
 *
 * Inputs:
 *   linkEnabled    — boolean: is user creating a shareable link?
 *   linkLabel      — string:  the label they entered (or blank for default)
 *   emailRecipientCount — number: how many people will be emailed
 *   deliveryMode   — 'now' | 'schedule' | 'birthday'
 *   scheduleDate   — Date or ISO string (only used when deliveryMode='schedule')
 *   birthdayTime   — "HH:MM" (only used when deliveryMode='birthday')
 *
 * If neither channel is configured, returns an empty array. Callers
 * (the creator's review step, the toast) decide how to display that.
 */
export function buildShareSummary({
  linkEnabled = false,
  linkLabel = '',
  emailRecipientCount = 0,
  deliveryMode = 'now',
  scheduleDate = null,
  birthdayTime = null,
} = {}) {
  const lines = [];

  // ── Shareable link line (always first when present) ──
  if (linkEnabled) {
    const labelDisplay = (linkLabel || '').trim();
    const labelClause = labelDisplay ? `, labeled "${labelDisplay}"` : '';
    lines.push({
      icon: '🔗',
      text: `You'll get a link to share${labelClause} — copied to your clipboard.`,
    });
  }

  // ── Email line(s) ──
  if (emailRecipientCount > 0) {
    const people = `${emailRecipientCount} ${emailRecipientCount === 1 ? 'person' : 'people'}`;

    if (deliveryMode === 'birthday') {
      const timeStr = birthdayTime ? formatBirthdayTimeOfDay(birthdayTime) : '9:00 AM';
      lines.push({
        icon: '🎂',
        text: `We'll email each of ${people} on their birthday at ${timeStr} (their local time).`,
      });
    } else if (deliveryMode === 'schedule') {
      const when = formatScheduledDateTime(scheduleDate);
      if (when) {
        lines.push({
          icon: '📅',
          text: `We'll email your card to ${people} on ${when}.`,
        });
      } else {
        // Defensive: scheduled mode with no date yet. The send button
        // is disabled in this state anyway, but we surface something
        // useful in the summary in case the UI is still being filled out.
        lines.push({
          icon: '📅',
          text: `We'll email your card to ${people} at the scheduled time.`,
        });
      }
    } else {
      // 'now' (default)
      lines.push({
        icon: '✉️',
        text: `We'll email your card to ${people} right now.`,
      });
    }
  }

  return lines;
}

// ─── Time Parsing Utilities ───────────────────────────────────────────────────

export function format24to12(time24) {
  if (!time24) return '';
  const parts = time24.split(':');
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1] || '00';
  if (isNaN(hours)) return '';
  const ampm = hours >= 12 ? 'PM' : 'AM';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${ampm}`;
}

export function parse12to24(input) {
  if (!input) return null;
  const str = input.trim();
  if (!str) return null;

  const s = str.toLowerCase().replace(/\s+/g, ' ').trim();

  const hasAM = /a\.?m\.?$/i.test(s);
  const hasPM = /p\.?m\.?$/i.test(s);
  const hasMeridiem = hasAM || hasPM;

  let timePart = s.replace(/\s*(a\.?m\.?|p\.?m\.?)$/i, '').trim();

  let hours, minutes;

  if (timePart.includes(':')) {
    const [h, m] = timePart.split(':');
    hours = parseInt(h, 10);
    minutes = parseInt(m, 10);
  } else if (timePart.length >= 3 && !timePart.includes(':')) {
    if (timePart.length === 3) {
      hours = parseInt(timePart[0], 10);
      minutes = parseInt(timePart.slice(1), 10);
    } else if (timePart.length === 4) {
      hours = parseInt(timePart.slice(0, 2), 10);
      minutes = parseInt(timePart.slice(2), 10);
    } else {
      return null;
    }
  } else {
    hours = parseInt(timePart, 10);
    minutes = 0;
  }

  if (isNaN(hours) || isNaN(minutes)) return null;
  if (minutes < 0 || minutes > 59) return null;

  if (hasMeridiem) {
    if (hours < 1 || hours > 12) return null;
    if (hasAM) {
      if (hours === 12) hours = 0;
    } else {
      if (hours !== 12) hours += 12;
    }
  } else {
    if (hours < 0 || hours > 23) return null;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// ─── UI Components ────────────────────────────────────────────────────────────

export function TimeInput({ value, onChange, className, inputClassName, placeholder }) {
  const [inputValue, setInputValue] = useState(() => format24to12(value));
  const [error, setError] = useState(null);

  useEffect(() => {
    const formatted = format24to12(value);
    if (formatted) {
      setInputValue(formatted);
      setError(null);
    }
  }, [value]);

  const handleBlur = () => {
    const parsed = parse12to24(inputValue);
    if (parsed) {
      setError(null);
      setInputValue(format24to12(parsed));
      onChange(parsed);
    } else if (inputValue.trim() === '') {
      setInputValue(format24to12(value));
      setError(null);
    } else {
      setError('Invalid time');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <div className={className}>
      <input
        type="text"
        value={inputValue}
        onChange={(e) => { setInputValue(e.target.value); setError(null); }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={placeholder || '8:00 AM'}
        className={inputClassName || 'w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold/50 transition-colors'}
      />
      {error && <p className="text-red-400 text-sm mt-1">{error}</p>}
    </div>
  );
}

export function ProgressSteps({ currentStep }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {SEND_STEPS.map((step, index) => {
        const isCompleted = step.id < currentStep;
        const isActive = step.id === currentStep;
        return (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm sm:text-base font-bold transition-colors ${
                  isCompleted
                    ? 'bg-gold text-royal-900'
                    : isActive
                    ? 'bg-gold text-royal-900 shadow-lg shadow-gold/20'
                    : 'bg-royal-800 border border-royal-600 text-white'
                }`}
              >
                {isCompleted ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step.id
                )}
              </div>
              <span
                className={`mt-1 sm:mt-2 text-sm font-medium whitespace-nowrap ${
                  isActive ? 'text-gold' : isCompleted ? 'text-gold-light' : 'text-royal-900 dark:text-white'
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < SEND_STEPS.length - 1 && (
              <div
                className={`h-0.5 w-6 sm:w-12 md:w-20 mx-1 sm:mx-2 mb-4 sm:mb-6 transition-colors ${
                  step.id < currentStep ? 'bg-gold' : 'bg-royal-700'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function UpgradeBanner({ isLoggedIn, message }) {
  return (
    <div className="p-5 bg-gold/10 border border-gold/30 rounded-xl text-center">
      <p className="text-gold font-semibold text-base mb-1">{message || 'Upgrade to send eCards to your friends and family'}</p>
      <p className="text-royal-100 text-sm mb-3">You can still browse and preview.</p>
      <div className="flex gap-3 justify-center">
        {isLoggedIn ? (
          <a href="/pricing" className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors">View Plans</a>
        ) : (
          <>
            <a href="/register" className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors">Create Account</a>
            <a href="/login" className="px-5 py-2.5 border border-royal-600 text-white rounded-lg text-base hover:border-royal-200 hover:text-white transition-colors">Log In</a>
          </>
        )}
      </div>
    </div>
  );
}

export function UsageStats({ usage, isLifetime }) {
  if (!usage) return null;
  if (isUnlimitedUsage(usage)) return null;

  const { used, limit, remaining } = usage;
  const percentUsed = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const isNearLimit = remaining <= 3;
  const isAtLimit = remaining === 0;

  return (
    <div className={`p-4 rounded-xl border ${isAtLimit ? 'bg-red-900/20 border-red-600/40' : isNearLimit ? 'bg-amber-900/20 border-amber-600/40' : 'bg-royal-800/40 border-royal-600'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-white text-sm sm:text-base">
          {isLifetime ? 'Lifetime Deliveries' : 'Monthly Deliveries'}
        </span>
        <span className={`text-sm sm:text-base font-medium ${isAtLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : 'text-white'}`}>
          {used} / {limit}
        </span>
      </div>
      <div className="w-full bg-royal-700 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all ${
            isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-gold'
          }`}
          style={{ width: `${percentUsed}%` }}
        />
      </div>
      <p className={`text-sm mt-2 ${isAtLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : 'text-royal-100'}`}>
        {isAtLimit
          ? 'You have reached your delivery limit.'
          : `${remaining} ${remaining !== 1 ? 'deliveries' : 'delivery'} remaining${isLifetime ? '' : ' this month'}`
        }
      </p>
      {isAtLimit && (
        <a href="/pricing" className="inline-block mt-2 text-base text-gold hover:text-gold-light underline transition-colors">
          Upgrade your plan →
        </a>
      )}
    </div>
  );
}